import { useEffect, useState, useSyncExternalStore } from 'react';
import type { ReactNode } from 'react';
import { Loader } from '../admin/loader.ts';
import type { Loaded } from '../admin/loader.ts';
import { ApiError, asSentence } from '../lib/http.ts';

export type { Loaded };

/**
 * The frame every admin page shares (docs/design-system.md, "Admin page"): a masthead over a 2px
 * ink rule, with the display title and one mono meta line, then the page's own sections.
 */
export function AdminPage({
  title,
  meta,
  children,
}: {
  title: string;
  /** Counts for the meta line. A page that has not loaded yet passes none. */
  meta?: string[];
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="admin-view">
      <header className="admin-masthead">
        <span className="label">admin</span>
        <h2 className="display admin-title">{title}</h2>
        {meta && meta.length > 0 && (
          <div className="admin-meta">
            {meta.map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>
        )}
      </header>
      {children}
    </div>
  );
}

/** The server's own words when it sent some, as a sentence. Guard messages are written to be shown. */
export function errorText(e: unknown): string {
  if (e instanceof ApiError) return asSentence(e.serverMessage ?? e.message);
  return asSentence(e instanceof Error ? e.message : String(e));
}

/**
 * Load once on mount, and again when asked. The rules are in admin/loader.ts: `reload` is a
 * button's reload and is ignored while a request is in flight, and `invalidate` is a write's
 * reload and is never dropped. `inFlight` lets a button say so and refuse the click.
 */
export function useLoaded<T>(load: () => Promise<T>): {
  state: Loaded<T>;
  inFlight: boolean;
  reload: () => void;
  invalidate: () => void;
  setData: (next: T) => void;
} {
  // One loader for the life of the page. Every caller passes a module-level function of
  // adminApi.ts, so the first `load` is the only one.
  const [loader] = useState(() => new Loader(load, errorText));
  const { state, inFlight } = useSyncExternalStore(loader.subscribe, loader.getSnapshot, loader.getSnapshot);

  useEffect(() => {
    void loader.refresh();
    return loader.dispose;
  }, [loader]);

  return {
    state,
    inFlight,
    reload: () => void loader.refresh(),
    invalidate: () => void loader.invalidate(),
    setData: loader.setData,
  };
}

/** The two states a page is in before it has data: one empty panel, or one notice and a retry. */
export function LoadState({
  state,
  inFlight,
  onRetry,
}: {
  state: { status: 'loading' } | { status: 'failed'; message: string };
  /** A retry is already on its way: the button says so, and takes no second click. */
  inFlight: boolean;
  onRetry: () => void;
}): JSX.Element {
  if (state.status === 'loading') {
    return (
      <div className="panel grat admin-loading">
        <span className="label">loading…</span>
      </div>
    );
  }
  return (
    <div className="admin-failed">
      <p className="notice err" role="alert">
        {state.message}
      </p>
      <button className="btn" type="button" disabled={inFlight} onClick={onRetry}>
        {inFlight ? 'Trying…' : 'Try again'}
      </button>
    </div>
  );
}
